/**
 * One Quixo Audit (Phase 1, G02/G03/G07): status timeline, downloads (PDF / XLSX through the signed-link API), included
 * re-runs with the number left, an upgrade CTA, and the web report rendered from the stored analysis
 * (GET /v1/growth-audit/jobs/:id/report): executive summary, six-signal board, top fixes, channel sweep, competitors, plan.
 * Works for website and social-only subjects (the heading shows the handle when there is no domain).
 * Viewers see everything but the write controls (re-run, upgrade).
 * A report shared by a Quixo workspace (sharedWithYou) is view and download only: no re-run, upgrade or resolve controls.
 * A Quixo internal workspace sees "Share with client" on its own finished audits (ShareWithClients).
 */
import { useState, type ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { Badge, Button, Card, CardHead, DataTable, EmptyState, ErrorState, Heading, Loading, PageHeader, ScoreRing, Text, type Column } from '@qm/ui';
import type { AuditAnalysis, Channel, CompetitorAnalysis, Finding } from '@qm/contracts/audit-analysis';
import { manualReviewFromAnalysis } from '@qm/contracts/manual-review';
import { useApp } from '../../../lib/app-context';
import { bffJson, BffError } from '../../../lib/bff';
import { useFlags } from '../../../lib/flags';
import { useFormat } from '../../../lib/format';
import { useResource, type ResourceState } from '../../../lib/resource';
import { BuyAudit } from './BuyAudit';
import { AdvancedReportLink } from './advanced/report';
import { ResolveFindings } from './ResolveFindings';
import { ShareWithClients } from './ShareWithClients';
import { GROWTH_AUDIT_MODULE_FLAG, type GrowthAuditModuleView } from './GrowthAuditModuleCard';
import { canWrite, statusTone } from './AuditPage';
import {
  auditErrorKey, gradeTone, severityTone, signalBoard, statusTimeline, subjectLabel, textFor, tierKey, topFixes, upgradeTierFor,
  type AuditJob, type AuditReport,
} from './audit-view';
import './audit.css';

/** Start a download: ask for a short-lived signed link, then let the browser fetch it (same origin, via the BFF). */
export async function downloadArtifact(jobId: string, kind: 'pdf' | 'xlsx', go: (url: string) => void = (u) => window.location.assign(u)): Promise<void> {
  const r = await bffJson<{ url: string }>(`/v1/growth-audit/jobs/${encodeURIComponent(jobId)}/artifacts/${kind}/link`, 'POST', {});
  if (typeof r.url === 'string' && r.url.startsWith('/v1/growth-audit/download/')) go(`/bff${r.url}`);
  else throw new Error('unexpected_link');
}

export function AuditDetailPage() {
  const { id = '' } = useParams();
  const job = useResource<AuditJob>(id ? `/v1/growth-audit/jobs/${encodeURIComponent(id)}` : null);
  const moduleOn = useFlags()(GROWTH_AUDIT_MODULE_FLAG) === 'on';
  const module = useResource<GrowthAuditModuleView>(moduleOn ? '/v1/growth-audit/module' : null);
  const ready = job.status === 'ready' && job.data.status === 'ready';
  const report = useResource<AuditReport>(ready ? `/v1/growth-audit/jobs/${encodeURIComponent(id)}/report?format=json` : null);
  return <AuditDetailView id={id} job={job} report={ready ? report : null} reload={job.reload}
    module={!moduleOn ? undefined : module.status==='ready'?module.data:null} />;
}

export function AuditDetailView({ job, report, reload, module }: { id: string; job: ResourceState<AuditJob>; report: ResourceState<AuditReport> | null; reload?: () => void; module?: GrowthAuditModuleView | null }) {
  const { t, me, locale } = useApp();
  const f = useFormat();
  const [params] = useSearchParams();
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);
  const writerRole = canWrite(me?.roles);
  const loc: 'en' | 'ar' = locale === 'ar' ? 'ar' : 'en';

  if (job.status === 'loading') return <Shell title={t('shell.p1.detail.title')}><Loading label={t('common.loading')} /></Shell>;
  if (job.status === 'error') {
    const missing = job.error instanceof BffError && job.error.status === 404;
    return <Shell title={t('shell.p1.detail.title')}>{missing ? <EmptyState title={t('shell.p1.detail.notFound')}><Link to="/audit">{t('shell.screens.audit.back')}</Link></EmptyState> : <ErrorState message={t('shell.error.load')} />}</Shell>;
  }
  const j = job.data;
  const shared = j.sharedWithYou === true;
  const writer = writerRole && !shared;
  const analysis = report?.status === 'ready' ? report.data.analysis : null;
  const subject = subjectLabel(j.targetUrl, analysis);
  const tk = tierKey(j.tier);
  const upgrade = upgradeTierFor(j.tier);
  const reruns = j.includedReruns ?? null;

  const run = async (key: string, fn: () => Promise<void>) => {
    setBusy(key); setMsg(null);
    try { await fn(); } catch (e) { setMsg({ tone: 'error', text: t(auditErrorKey(e)) }); } finally { setBusy(null); }
  };
  const rerun = () => run('rerun', async () => {
    await bffJson(`/v1/growth-audit/jobs/${encodeURIComponent(j.id)}/rerun`, 'POST', {});
    setMsg({ tone: 'ok', text: t('shell.p1.detail.rerunStarted') });
    reload?.();
  });

  return (
    <Shell title={subject.label} eyebrow={[tk ? t(`shell.p1.tier.${tk}`) : null, subject.platform ? t(`shell.p1.platform.${subject.platform}`) : null].filter(Boolean).join(' · ')}
      actions={<Link className="qm-text-link" to="/audit">{t('shell.screens.audit.back')}</Link>}>
      {params.get('order') ? <Card role="status" className="qm-audit__banner" data-tone={params.get('cancelled') === '1' ? 'warning' : 'success'}><Text>{t(params.get('cancelled') === '1' ? 'shell.p1.list.cancelled' : 'shell.p1.detail.upgradeReceived')}</Text></Card> : null}

      {shared ? <Card role="status" className="qm-audit__banner" data-tone="success"><Text>{t('shell.p1.share.received')}</Text></Card> : null}

      <div className="qm-audit__grid">
        <Card aria-labelledby="audit-status">
          <CardHead id="audit-status" title={t('shell.p1.detail.status')} trailing={<Badge tone={statusTone(j.status)}>{t(`shell.screens.auditDash.status.${j.status}`)}</Badge>} />
          <ol className="qm-timeline">
            {statusTimeline(j).map((s) => (
              <li key={s.step} className="qm-timeline__step" data-state={s.state}>
                <b>{t(`shell.p1.timeline.${s.step}`)}</b>
                <span>{s.state === 'delayed' ? t('shell.p1.timeline.delayed') : s.at ? f.dateTime(s.at) : t(`shell.p1.timeline.state.${s.state}`)}</span>
              </li>
            ))}
          </ol>
          {j.status === 'delayed' ? <Text role="status">{t('shell.screens.auditDash.delayedHelp')}</Text> : null}
        </Card>

        <Card aria-labelledby="audit-files">
          <CardHead id="audit-files" title={t('shell.p1.detail.files')} />
          {j.status !== 'ready' || !j.artifacts.length ? <Text tone="muted">{t('shell.p1.detail.filesPending')}</Text> : (
            <div className="qm-row">
              {j.artifacts.map((a) => (
                <Button key={a.kind} variant={a.kind === 'pdf' ? 'primary' : 'secondary'} disabled={busy === a.kind}
                  onClick={() => void run(a.kind, () => downloadArtifact(j.id, a.kind))}>
                  {t(a.kind === 'pdf' ? 'shell.p1.detail.downloadPdf' : 'shell.p1.detail.downloadXlsx')}
                </Button>
              ))}
            </div>
          )}
          {reruns ? (
            <div className="qm-audit__reruns">
              <Text>{t('shell.p1.detail.rerunsLeft', { left: f.number(reruns.remaining), included: f.number(reruns.included) })}{reruns.until ? ` · ${t('shell.screens.auditDash.until')} ${f.date(reruns.until)}` : ''}</Text>
              {writer ? (
                <Button disabled={!reruns.canRerun || busy === 'rerun'} onClick={() => void rerun()}>{t('shell.p1.detail.rerun')}</Button>
              ) : null}
              {!reruns.canRerun && reruns.reason ? <Text tone="muted">{t(`shell.p1.err.rerun_${reruns.reason}`)}</Text> : null}
            </div>
          ) : null}
          {msg ? <Text role={msg.tone === 'error' ? 'alert' : 'status'}>{msg.text}</Text> : null}
        </Card>
      </div>

      {j.status === 'ready' && !shared ? <AdvancedReportLink jobId={j.id} /> : null}
      {j.status === 'ready' && !shared ? <ShareWithClients jobId={j.id} /> : null}
      {j.status !== 'ready' ? null : !report || report.status === 'loading' ? <Loading label={t('common.loading')} /> : report.status === 'error' ? (
        <Card><Text tone="muted">{t(report.error instanceof BffError && report.error.status === 404 ? 'shell.p1.report.unavailable' : auditErrorKey(report.error, 'shell.error.load'))}</Text></Card>
      ) : <>
        <WebReport a={report.data.analysis} loc={loc} />
        {module !== undefined && !shared ? <ResolveFindings auditId={j.id} findings={report.data.analysis.findings} module={module} writer={writer} /> : null}
      </>}

      {writer && upgrade ? (
        <Card aria-labelledby="audit-upgrade">
          <CardHead id="audit-upgrade" title={t('shell.p1.detail.upgradeTitle', { tier: t(`shell.p1.tier.${upgrade === 'pro' ? 'professional' : upgrade}`) })} sub={t(`shell.p1.detail.upgradeSub.${upgrade}`)} />
          <BuyAudit defaultTier={upgrade} tiers={upgrade === 'pro' ? ['pro', 'enterprise'] : ['enterprise']} subject={j.targetUrl} fromJobId={j.id} compact />
        </Card>
      ) : null}

      <Text tone="muted">{t('shell.p1.detail.help')} <Link to="/help">{t('shell.p1.detail.helpLink')}</Link></Text>
    </Shell>
  );
}

function Shell({ title, eyebrow, actions, children }: { title: string; eyebrow?: string; actions?: ReactNode; children: ReactNode }) {
  return (
    <section className="qm-page qm-audit" aria-labelledby="page-title">
      <PageHeader title={title} {...(eyebrow ? { eyebrow } : {})} {...(actions ? { actions } : {})} />
      {children}
    </section>
  );
}

/** Text of the report in the reader's language; English fallbacks carry lang="en". */
function L({ v, loc, as: Tag = 'span' }: { v: Parameters<typeof textFor>[0]; loc: 'en' | 'ar'; as?: 'span' | 'p' | 'b' }) {
  const x = textFor(v, loc);
  if (!x) return null;
  return <Tag lang={x.lang !== loc ? x.lang : undefined} dir={x.lang !== loc ? 'auto' : undefined}>{x.text}</Tag>;
}

export function WebReport({ a, loc }: { a: AuditAnalysis; loc: 'en' | 'ar' }) {
  const { t } = useApp();
  const f = useFormat();
  const fixes = topFixes(a, 5);
  const findingById = new Map(a.findings.map((x) => [x.id, x]));
  const manual = manualReviewFromAnalysis(a);
  return (
    <div className="qm-report">
      <Card aria-labelledby="report-summary" className="qm-report__summary">
        <CardHead id="report-summary" title={t('shell.p1.report.summary')} />
        <div className="qm-report__hero">
          {a.overall !== null ? <ScoreRing value={a.overall} label={t('shell.p1.report.overall')} size="lg" /> : <Text>{t('shell.screens.auditDash.notMeasured')}</Text>}
          <L v={a.executiveSummary} loc={loc} as="p" />
        </div>
      </Card>

      <Card aria-labelledby="report-signals">
        <CardHead id="report-signals" title={t('shell.p1.report.signals')} sub={t('shell.p1.report.signalsSub')} />
        <ul className="qm-signals">
          {signalBoard(a).map((s) => (
            <li key={s.id} className="qm-signal">
              <div className="qm-signal__head">
                <b>{t(`shell.p1.signal.${s.id}`)}</b>
                <Badge tone={gradeTone(s.grade)}>{s.score === null ? t('shell.screens.auditDash.notMeasured') : `${f.number(s.score)} · ${s.grade}`}</Badge>
              </div>
              <L v={s.headline} loc={loc} as="p" />
              <details>
                <summary>{t('shell.p1.report.more')}</summary>
                <L v={s.whatWeFound} loc={loc} as="p" />
                <L v={s.whyItMatters} loc={loc} as="p" />
              </details>
            </li>
          ))}
        </ul>
      </Card>

      <Card aria-labelledby="report-fixes">
        <CardHead id="report-fixes" title={t('shell.p1.report.topFixes')} sub={t('shell.p1.report.topFixesSub')} />
        {!fixes.length ? <Text tone="muted">{t('shell.p1.report.noFindings')}</Text> : (
          <ol className="qm-findings">
            {fixes.map((x) => <FindingItem key={x.id} f={x} loc={loc} />)}
          </ol>
        )}
      </Card>

      {a.channels.length ? (
        <Card aria-labelledby="report-channels">
          <CardHead id="report-channels" title={t('shell.p1.report.channels')} />
          <DataTable caption={t('shell.p1.report.channels')} columns={[
            { key: 'channel', header: t('shell.p1.col.channel'), render: (c) => (c.url && /^https:\/\//.test(c.url) ? <a href={c.url} rel="noopener noreferrer" target="_blank"><bdi>{f.text(c.channel)}</bdi></a> : <bdi>{f.text(c.channel)}</bdi>) },
            { key: 'status', header: t('shell.screens.auditDash.status'), render: (c) => <Badge tone={c.status === 'active' ? 'success' : c.status === 'not_measured' ? 'neutral' : c.status === 'dormant' ? 'warning' : 'error'}>{t(`shell.p1.channel.${c.status}`)}</Badge> },
            { key: 'found', header: t('shell.p1.col.found'), render: (c) => <L v={c.whatWeFound} loc={loc} /> },
          ] satisfies Column<Channel & { id: string }>[]} rows={a.channels.map((c, i) => ({ ...c, id: `${c.channel}-${i}` }))} />
        </Card>
      ) : null}

      {a.competitors.length ? (
        <Card aria-labelledby="report-competitors">
          <CardHead id="report-competitors" title={t('shell.p1.report.competitors')} />
          <DataTable caption={t('shell.p1.report.competitors')} columns={[
            { key: 'name', header: t('shell.p1.col.competitor'), render: (c) => <bdi>{f.text(c.domain ?? c.name)}</bdi> },
            { key: 'overall', header: t('shell.screens.auditDash.score'), numeric: true, render: (c) => (c.overall === null ? t('shell.screens.auditDash.notMeasured') : f.number(c.overall)) },
            { key: 'edge', header: t('shell.p1.col.edge'), render: (c) => <L v={c.edge} loc={loc} /> },
          ] satisfies Column<CompetitorAnalysis & { id: string }>[]} rows={a.competitors.map((c, i) => ({ ...c, id: `${c.name}-${i}` }))} />
        </Card>
      ) : null}

      <Card aria-labelledby="report-plan">
        <CardHead id="report-plan" title={t('shell.p1.report.plan')} />
        <div className="qm-plan">
          {(['quickWins', 'days30', 'days90'] as const).map((b) => (
            <div key={b} className="qm-plan__col">
              <Heading level={3}>{t(`shell.p1.plan.${b}`)}</Heading>
              {a.plan[b].length ? (
                <ul>{a.plan[b].map((fid) => { const x = findingById.get(fid); return x ? <li key={fid}><L v={x.title} loc={loc} /></li> : null; })}</ul>
              ) : <Text tone="muted">{t('shell.p1.plan.none')}</Text>}
            </div>
          ))}
        </div>
      </Card>

      {a.coverage.notMeasured.length ? (
        <Card aria-labelledby="report-notmeasured">
          <CardHead id="report-notmeasured" title={t('shell.p1.report.notMeasured')} sub={t('shell.p1.report.notMeasuredSub')} />
          {manual ? <Text className="qm-report__manual"><a href={manual.mailto} lang={loc} dir="auto">{manual[loc]}</a></Text> : null}
          <ul className="qm-list">{a.coverage.notMeasured.map((n) => <li key={n.ref} className="qm-list__item"><bdi>{f.text(n.ref)}</bdi><span lang="en" dir="auto">{n.reason}</span></li>)}</ul>
        </Card>
      ) : null}
    </div>
  );
}

function FindingItem({ f: x, loc }: { f: Finding; loc: 'en' | 'ar' }) {
  const { t } = useApp();
  return (
    <li className="qm-finding">
      <div className="qm-finding__head">
        <Badge tone={severityTone(x.severity)}>{t(`shell.p1.severity.${x.severity}`)}</Badge>
        <L v={x.title} loc={loc} as="b" />
      </div>
      <dl className="qm-finding__body">
        <dt>{t('shell.p1.finding.evidence')}</dt><dd><L v={x.evidence} loc={loc} /></dd>
        <dt>{t('shell.p1.finding.impact')}</dt><dd><L v={x.impact} loc={loc} /></dd>
        <dt>{t('shell.p1.finding.fix')}</dt><dd><L v={x.fix} loc={loc} /></dd>
        <dt>{t('shell.p1.finding.who')}</dt><dd>{t(`shell.p1.owner.${x.owner}`)} · {t(`shell.p1.effort.${x.effort}`)}</dd>
      </dl>
    </li>
  );
}

